import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './i18n.ts';

Sentry.init({
  dsn:
    import.meta.env.VITE_SENTRY_ENVIRONMENT === 'development'
      ? undefined
      : 'https://6b326d8efdabf0e6fe439560964ae2bf@o4510907298938880.ingest.de.sentry.io/4510917715492944',
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  sendDefaultPii: true,
});

import './index.css';

import { ChakraProvider, LocaleProvider } from '@chakra-ui/react';
import { ThemeProvider } from 'next-themes';
import { sindiTheme } from '../chakra-theme.ts';
import AuthProvider from './context/AuthProvider.tsx';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Router from './Router.tsx';
import { ModalProvider } from './context/ModalContext.tsx';
import { AppTracking } from './components/AppTracking.tsx';
import { useTranslation } from 'react-i18next';

const queryClient = new QueryClient();

const localeMap: Record<string, string> = {
  sv: 'sv-SE',
  en: 'en-GB',
};

function App() {
  const { i18n } = useTranslation();
  const locale = localeMap[i18n.language] ?? 'en-GB';

  return (
    <LocaleProvider locale={locale}>
      <AuthProvider>
        <AppTracking />
        <ModalProvider>
          <Router />
        </ModalProvider>
      </AuthProvider>
    </LocaleProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ChakraProvider value={sindiTheme}>
        <ThemeProvider
          attribute="class"
          forcedTheme="light"
          disableTransitionOnChange
        >
          <App />
        </ThemeProvider>
      </ChakraProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </StrictMode>
);
